import type { BridgeHealthResult } from '../../types/acombaBridge';
import type { BridgeCause, LaunchedBridgeStatus } from './bridgeCause';

export interface BridgeDiagnosticsInput {
    cause: BridgeCause;
    health: BridgeHealthResult | null;
    /** What Acomba reported when it opened this window, when it said anything. */
    launched: LaunchedBridgeStatus | null;
    /** The address the client tried, which is not always the one the bridge is on. */
    attemptedUrl: string | null;
    now: Date;
    userAgent: string;
}

/**
 * Bridge-side facts only — no customer, user or company identity, because this gets pasted into email
 * and ticket systems. Wire field names, never translated: two postes in different languages must
 * produce comparable dumps, or one fault reads as two.
 */
export function buildBridgeDiagnostics(input: BridgeDiagnosticsInput): string {
    const { cause, health, launched, attemptedUrl, now, userAgent } = input;

    const lines: string[] = [
        'ComptaAI Acomba bridge diagnostics',
        `generatedAt: ${now.toISOString()}`,
        `cause: ${cause}`,
        `launchedStatus: ${launched ?? '(not reported)'}`,
        `attemptedUrl: ${attemptedUrl ?? '(none)'}`,
    ];

    if (!health) {
        lines.push('health: (no response)');
    } else {
        lines.push(
            'health:',
            `  bridgeVersion: ${health.bridgeVersion || '(unknown)'}`,
            `  sdkVersion: ${health.sdkVersion ?? '(absent)'}`,
            `  sdkSupported: ${health.sdkSupported ?? '(not reported)'}`,
            `  apiContract: ${health.apiContract ?? '(not reported)'}`,
            `  mode: ${health.mode ?? '(not reported)'}`,
            `  boundUrl: ${health.boundUrl ?? '(not reported)'}`,
            `  endpointPath: ${health.endpointPath ?? '(not reported)'}`,
            `  lastStartupError: ${health.lastStartupError ?? '(none)'}`,
            // Every caller but the browser ignores certificate dates, so an expired one reads as a
            // healthy bridge everywhere else.
            `  certificateNotAfter: ${health.certificateNotAfter ?? '(not reported)'}`,
            `  timestamp: ${health.timestamp || '(not reported)'}`,
        );
    }

    lines.push(`userAgent: ${userAgent}`);

    return lines.join('\n');
}

/** False rather than throwing when the clipboard is unavailable: a non-secure origin, or a browser
 * that withholds the permission. */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        if (!navigator.clipboard?.writeText) return false;
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}
